(() => {
  if (window.customElements.get("product-media")) return;

// js/custom-element/section/product/product-media.js
var ProductMedia = class extends CustomHTMLElement {
  async connectedCallback() {
    const connection = this._connection = {};
    this.mainCarousel = this.querySelector("flickity-carousel");
    this._setupThumbnails();
    this.zoomButton = this.querySelector(".product__zoom-button");
    this.pocketQuery = window.matchMedia(window.themeVariables.breakpoints.pocket);
    this.listenTo(this, "click", this._onImageClick.bind(this));
    if (this.hasAttribute("mobile-draggable")) this.listenTo(this.pocketQuery, "change", this._syncDraggability.bind(this));
    if (this.hasAttribute("reveal-on-scroll")) {
      this._setupVisibility();
    }
    if (!this.mainCarousel || this.mainCarousel.querySelectorAll("[data-media-id]").length <= 1) {
      return;
    }
    this.selectedVariantMediaId = null;
    this.viewInSpaceElement = this.querySelector("[data-shopify-model3d-id]");
    this.listenTo(this.mainCarousel, "model:played", () => this.mainCarousel.setDraggable(false));
    this.listenTo(this.mainCarousel, "model:paused", this._syncDraggability.bind(this));
    this.listenTo(this.mainCarousel, "flickity:ready", this._onFlickityReady.bind(this));
    this.listenTo(this.mainCarousel, "flickity:slide-changed", this._onFlickityChanged.bind(this));
    this.listenTo(this.mainCarousel, "flickity:slide-settled", this._onFlickitySettled.bind(this));
    this._onFlickityReady();
    const form = document.getElementById(this.getAttribute("form-id"));
    if (form) this.listenTo(form, "variant:change", this._onVariantChanged.bind(this));
    try {
      const product = await ProductLoader.load(this.getAttribute("product-handle"));
      if (!this.isConnected || connection !== this._connection) return;
      this.product = product;
    } catch {}
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this._connection = null;
    this._variantChange = null;
    this._revealAnimation?.cancel();
    this.thumbnailObserver?.disconnect();
  }
  _loadImages(root) {
    root?.querySelectorAll("[data-gallery-src]").forEach(image => {
      if (image.dataset.gallerySrcset) image.srcset = image.dataset.gallerySrcset;
      image.src = image.dataset.gallerySrc;
      image.removeAttribute("data-gallery-srcset");
      image.removeAttribute("data-gallery-src");
      image.removeAttribute("aria-hidden");
    });
  }
  _setupThumbnails() {
    this.thumbnailObserver?.disconnect();
    const thumbnails = this.querySelectorAll(".product__thumbnail-item");
    if (window.IntersectionObserver && !window.Shopify?.designMode) {
      this.thumbnailObserver = new IntersectionObserver(entries => {
        if (!this.isConnected) return;
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          this._loadImages(entry.target);
          this.thumbnailObserver.unobserve(entry.target);
        });
      }, { rootMargin: "80px" });
      thumbnails.forEach(item => {
        if (item.querySelector("[data-gallery-src]")) this.thumbnailObserver.observe(item);
      });
    } else {
      this._loadImages(this);
    }
    this.listenTo(this, "focusin", event => this._loadImages(event.target.closest(".product__thumbnail-item")));
  }
  _loadSelectedImages(instance) {
    instance.cells.slice(Math.max(0, instance.selectedIndex - 1), instance.selectedIndex + 2).forEach(cell => this._loadImages(cell.element));
  }
  get thumbnailsPosition() {
    return window.matchMedia(window.themeVariables.breakpoints.pocket).matches ? "bottom" : this.getAttribute("thumbnails-position");
  }
  _onImageClick(event) {
    if (event.defaultPrevented || !this.zoomButton) return;
    const image = event.target.closest(".product__media-image-wrapper");
    const slide = image?.closest('.product__media-item[data-media-type="image"]');
    if (!slide || slide.classList.contains("is-filtered") || !slide.classList.contains("is-selected")) return;
    event.preventDefault();
    this.zoomButton.click();
  }
  _syncDraggability() {
    this.mainCarousel?.setDraggable(!this.hasAttribute("mobile-draggable") || this.pocketQuery.matches);
  }
  async _setupVisibility() {
    const connection = this._connection;
    await this.untilVisible();
    if (!this.isConnected || connection !== this._connection || !this.mainCarousel) return;
    const flickityInstance = await this.mainCarousel.flickityInstance, image = flickityInstance ? flickityInstance.selectedElement.querySelector("img") : this.querySelector(".product__media-image-wrapper img"), prefersReducedMotion = MediaFeatures.prefersReducedMotion();
    await imageLoaded(image);
    if (!this.isConnected || connection !== this._connection || !image) return;
    const animation = this._revealAnimation = new CustomAnimation(new ParallelEffect([
      new CustomKeyframeEffect(image, { opacity: [0, 1], transform: [`scale(${prefersReducedMotion ? 1 : 1.1})`, "scale(1)"] }, { duration: 500, easing: "cubic-bezier(0.65, 0, 0.35, 1)" }),
      new ParallelEffect(Array.from(this.querySelectorAll(".product__thumbnail-item:not(.is-filtered)")).map((item, index) => {
        return new CustomKeyframeEffect(item, {
          opacity: [0, 1],
          transform: this.thumbnailsPosition === "left" ? [`translateY(${prefersReducedMotion ? 0 : "40px"})`, "translateY(0)"] : [`translateX(${prefersReducedMotion ? 0 : "50px"})`, "translateX(0)"]
        }, {
          duration: 250,
          delay: prefersReducedMotion ? 0 : 100 * index,
          easing: "cubic-bezier(0.75, 0, 0.175, 1)"
        });
      }))
    ]));
    this._hasSectionReloaded ? animation.finish() : animation.play();
  }
  async _onVariantChanged(event) {
    const variant = event.detail.variant;
    if (!variant) return;
    const change = this._variantChange = {};
    if (!this.product) {
      try { this.product = await ProductLoader.load(this.getAttribute("product-handle")); } catch { return; }
    }
    if (!this.isConnected || change !== this._variantChange) return;
    const filteredMediaIds = [];
    this.product["media"].forEach((media) => {
      let matchMedia2 = variant["featured_media"] && media["id"] === variant["featured_media"]["id"];
      if (media["alt"]?.includes("#")) {
        if (!matchMedia2) {
          const altParts = media["alt"].split("#"), mediaGroupParts = altParts.pop().split("_");
          this.product["options"].forEach((option) => {
            if (option["name"].toLowerCase() === mediaGroupParts[0].toLowerCase()) {
              if (variant["options"][option["position"] - 1].toLowerCase() !== mediaGroupParts[1].trim().toLowerCase()) {
                filteredMediaIds.push(media["id"]);
              }
            }
          });
        }
      }
    });
    const currentlyFilteredIds = [...new Set(Array.from(this.querySelectorAll(".is-filtered[data-media-id]")).map((item) => parseInt(item.getAttribute("data-media-id"))))];
    if (currentlyFilteredIds.some((value) => !filteredMediaIds.includes(value))) {
      const selectedMediaId = variant["featured_media"] ? variant["featured_media"]["id"] : this.product["media"].map((item) => item.id).filter((item) => !filteredMediaIds.includes(item))[0];
      const flickityInstance = await this.mainCarousel.flickityInstance;
      if (!this.isConnected || change !== this._variantChange || !flickityInstance) return;
      Array.from(this.querySelectorAll("[data-media-id]")).forEach((item) => {
        item.classList.toggle("is-filtered", filteredMediaIds.includes(parseInt(item.getAttribute("data-media-id"))));
        item.classList.toggle("is-selected", selectedMediaId === parseInt(item.getAttribute("data-media-id")));
        item.classList.toggle("is-initial-selected", selectedMediaId === parseInt(item.getAttribute("data-media-id")));
      });
      this.mainCarousel.reload();
    } else {
      if (!event.detail.variant["featured_media"] || this.selectedVariantMediaId === event.detail.variant["featured_media"]["id"]) {
        return;
      }
      this.mainCarousel.select(`[data-media-id="${event.detail.variant["featured_media"]["id"]}"]`);
    }
    this.selectedVariantMediaId = event.detail.variant["featured_media"] ? event.detail.variant["featured_media"]["id"] : null;
  }
  async _onFlickityReady() {
    const connection = this._connection;
    const flickityInstance = await this.mainCarousel?.flickityInstance;
    if (!this.isConnected || connection !== this._connection || !flickityInstance?.selectedElement) return;
    this._loadSelectedImages(flickityInstance);
    this._syncDraggability();
    if (["video", "external_video"].includes(flickityInstance.selectedElement.getAttribute("data-media-type")) && this.hasAttribute("autoplay-video")) {
      flickityInstance.selectedElement.firstElementChild.play();
    }
  }
  async _onFlickityChanged() {
    const connection = this._connection;
    const flickityInstance = await this.mainCarousel?.flickityInstance;
    if (!this.isConnected || connection !== this._connection || !flickityInstance) return;
    this._loadSelectedImages(flickityInstance);
    flickityInstance.cells.forEach((item) => {
      if (["external_video", "video", "model"].includes(item.element.getAttribute("data-media-type"))) {
        item.element.firstElementChild.pause();
      }
    });
  }
  async _onFlickitySettled() {
    const connection = this._connection;
    const flickityInstance = await this.mainCarousel?.flickityInstance;
    if (!this.isConnected || connection !== this._connection || !flickityInstance?.selectedElement) return;
    const selectedSlide = flickityInstance.selectedElement;
    if (this.zoomButton) {
      this.zoomButton.hidden = selectedSlide.getAttribute("data-media-type") !== "image";
    }
    if (this.viewInSpaceElement) {
      this.viewInSpaceElement.setAttribute("data-shopify-model3d-id", this.viewInSpaceElement.getAttribute("data-shopify-model3d-default-id"));
    }
    switch (selectedSlide.getAttribute("data-media-type")) {
      case "model":
        this.viewInSpaceElement?.setAttribute("data-shopify-model3d-id", selectedSlide.getAttribute("data-media-id"));
        selectedSlide.firstElementChild.play();
        break;
      case "external_video":
      case "video":
        if (this.hasAttribute("autoplay-video")) {
          selectedSlide.firstElementChild.play();
        }
        break;
    }
  }
};
window.customElements.define("product-media", ProductMedia);

})();
